import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { CoilSymbol } from '../primitives/CoilSymbol';
import { DT } from '../tokens';

/** Q_l = 0.68·CFM·ΔW — humid in → drier out, cooling coil with condensate drain. */
export function DiagramLatentHeat() {
  const dy = 100, dh = 65;
  const cy = dy + dh / 2;

  return (
    <DiagramCanvas>
      {/* Humid entering */}
      <Rect x={20} y={dy} width={70} height={dh} fill="rgba(122,170,187,0.18)" stroke={DT.stroke} strokeWidth="1.5" />
      <Line x1={28} y1={cy} x2={84} y2={cy} stroke={DT.arrow} strokeWidth="1.6" />
      <Polygon points={`86,${cy} 80,${cy - 4} 80,${cy + 4}`} fill={DT.arrow} />
      {/* Many moisture droplets */}
      {[35, 50, 65, 78, 42, 58, 70, 50, 60].map((px, i) => {
        const py = dy + 10 + (i * 7) % (dh - 20);
        return <Circle key={i} cx={px} cy={py} r={1.5} fill="#7AAABB" opacity={0.85} />;
      })}

      {/* Cooling coil */}
      <CoilSymbol x={90} y={dy} width={150} height={dh} loops={5} />

      {/* Condensate drain pan */}
      <Path d={`M90,${dy + dh} L240,${dy + dh} L240,${dy + dh + 8} L90,${dy + dh + 8} Z`}
        fill={DT.fill} stroke={DT.stroke} strokeWidth="1" />
      {/* Drain pipe */}
      <Line x1={165} y1={dy + dh + 8} x2={165} y2={dy + dh + 28} stroke={DT.stroke} strokeWidth="2" />
      {/* Water drops in pan */}
      {[110, 140, 175, 205, 225].map((px, i) => (
        <Circle key={i} cx={px} cy={dy + dh + 4} r={1.4} fill="#7AAABB" />
      ))}

      {/* Drier leaving */}
      <Rect x={240} y={dy} width={80} height={dh} fill="rgba(122,170,187,0.08)" stroke={DT.stroke} strokeWidth="1.5" />
      <Line x1={248} y1={cy} x2={310} y2={cy} stroke={DT.arrow} strokeWidth="1.6" />
      <Polygon points={`312,${cy} 306,${cy - 4} 306,${cy + 4}`} fill={DT.arrow} />
      {/* Few droplets only */}
      {[260, 285].map((px, i) => (
        <Circle key={i} cx={px} cy={dy + 30} r={1.5} fill="#7AAABB" opacity={0.5} />
      ))}

      {/* Humidity ratio badges */}
      <Rect x={28} y={dy - 22} width={52} height={14} fill="rgba(122,170,187,0.85)" rx="2" />
      <SvgText x={54} y={dy - 12} fill="#0D1B2A" fontSize="8.5" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">W₁ high</SvgText>
      <Rect x={250} y={dy - 22} width={52} height={14} fill="rgba(122,170,187,0.5)" rx="2" />
      <SvgText x={276} y={dy - 12} fill="#0D1B2A" fontSize="8.5" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">W₂ low</SvgText>

      {/* ΔW brace */}
      <Line x1={54} y1={dy - 38} x2={276} y2={dy - 38} stroke={DT.varColor} strokeWidth="1" />
      <Polygon points={`54,${dy - 38} 60,${dy - 35} 60,${dy - 41}`} fill={DT.varColor} />
      <Polygon points={`276,${dy - 38} 270,${dy - 35} 270,${dy - 41}`} fill={DT.varColor} />
      <SvgText x={165} y={dy - 42} fill={DT.varColor} fontSize="10" fontFamily={DT.mono} textAnchor="middle" fontWeight="700">ΔW = W₁ − W₂</SvgText>

      {/* Labels */}
      <LeaderLine x1={55} y1={cy} x2={20} y2={210} varName="CFM" desc="airflow" anchor="start" />
      <LeaderLine x1={165} y1={dy + dh + 24} x2={170} y2={210} varName="condensate" desc="moisture out" anchor="middle" />
      <LeaderLine x1={280} y1={cy} x2={310} y2={210} varName="Q_l" desc="latent BTU/h" anchor="end" />

      <FormulaTag formula="Q_l = 0.68 · CFM · ΔW · 7000   (per gr H₂O / lb dry air)" />
    </DiagramCanvas>
  );
}
